import type { StoryObject, StoryCategory } from '@benzinga/advanced-news-manager';
// import { AdvancedNewsManager, StoryCategory, nodeHasChannel, nodeHasTag } from '@benzinga/advanced-news-manager';
import type { Session } from '@benzinga/session';
import type { ICalendarKeys } from '@benzinga/calendars';
import type { ArticleData } from '../entities/article';
import { getInitialTickers } from './article';

const BENZINGA_NEWSDESK_AUTHOR_ID = 80598;

const BENZINGA_NEWSDESK_AUTHOR_NAMES = ['Benzinga News Desk', 'Benzinga Newsdesk'];

/** True when SoFi referral and article is from Benzinga News Desk (pro-only style). Used to show full headline instead of truncating. */
export const isFullHeadline = (authorIdOrName: number | string | undefined, utmSource: string | null): boolean => {
  if (utmSource !== 'sofi.com') return false;
  if (typeof authorIdOrName === 'number') return authorIdOrName === BENZINGA_NEWSDESK_AUTHOR_ID;
  if (typeof authorIdOrName === 'string') {
    const asNumber = Number(authorIdOrName);
    if (!Number.isNaN(asNumber)) return asNumber === BENZINGA_NEWSDESK_AUTHOR_ID;
    return BENZINGA_NEWSDESK_AUTHOR_NAMES.includes(authorIdOrName);
  }
  return false;
};

export const isProOnlyPost = (article: StoryObject | ArticleData, bodyCheck = false): boolean => {
  if (article) {
    const benzingaProAuthorIds = [11389];
    if (typeof article.author === 'string' && BENZINGA_NEWSDESK_AUTHOR_NAMES.includes(article.author)) {
      return true;
    }
    const author = 'userId' in article ? article.userId : article.author?.uid;
    if (author && benzingaProAuthorIds.includes(author)) {
      return true;
    }
    const result = !!((bodyCheck && !article['body']) || (article.isBzProPost && !article.isBzPost));
    return result;
  }
  return false;
};

export const isOfferingArticle = (article: ArticleData) => {
  if (article) {
    const tags = article.tags?.map(tag => tag.tid) ?? [];
    const channels = article.channels?.map(channel => channel.tid) ?? [];
    const tagsResult = tags.includes(16786);
    const channelsResult = channels.includes(142057);
    return tagsResult || channelsResult;
  }
  return false;
};

interface AllowedChannelI {
  articleChannel: string;
  calendarKey: ICalendarKeys;
}

export const getInitialTab = (article: ArticleData): ICalendarKeys | null => {
  if (!Array.isArray(article.channels)) {
    return null;
  }

  const allowedChannels: AllowedChannelI[] = [
    {
      articleChannel: 'earnings',
      calendarKey: 'earnings',
    },
    {
      articleChannel: 'analyst ratings',
      calendarKey: 'ratings',
    },
    {
      articleChannel: 'options',
      calendarKey: 'option_activity',
    },
    {
      articleChannel: 'dividends',
      calendarKey: 'dividends',
    },
  ];

  const formattedChannels: string[] =
    article.channels.length > 0 ? article.channels.map(el => el.name.toLowerCase()) : [];
  const intersections: ICalendarKeys[] = allowedChannels
    .filter(item => formattedChannels.includes(item.articleChannel))
    .map(item => item.calendarKey);

  return intersections?.[0] ? intersections[0] : null;
};

export const checkIfShouldLoadCalendar = (article: ArticleData): boolean => {
  const initialTab = getInitialTab(article);

  if (!initialTab || !getInitialTickers(article)?.length) {
    return false;
  } else {
    return true;
  }
};

export const isStoryTaggedPressRelease = async (
  session: Session,
  article: { channels: StoryCategory[]; tags: StoryCategory[]; type: string },
) => {
  const { AdvancedNewsManager, nodeHasChannel, nodeHasTag } = await import('@benzinga/advanced-news-manager');
  const advancedNewsManager = session.getManager(AdvancedNewsManager);
  const contentTypes = await advancedNewsManager.getContentTypes();

  const result =
    nodeHasChannel(article as unknown as StoryObject, 'Press Releases') ||
    nodeHasTag(article as unknown as StoryObject, 'Press Releases') ||
    (Array.isArray(contentTypes?.ok?.pressRelease) && contentTypes?.ok?.pressRelease.includes(article?.type));

  return result;
};

export const automatedContentAuthorIds = [178351]; //Benzinga insights

export const isContentAutomated = (userId: number) => {
  if (!userId) return false;
  return automatedContentAuthorIds.includes(userId) ?? false;
};

export const isTagged = (article: Pick<ArticleData, 'tags'>, tids: number[]) => {
  return article.tags?.some(tag => tids.includes(tag?.tid));
};

export const isChanneled = (article: Pick<ArticleData, 'channels'>, tids: number[]) => {
  return article.channels?.some(channel => tids.includes(channel?.tid));
};
